import * as React from 'react';
import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { cn } from '../lib/utils';
import { Button } from './button';
import { MarkdownContent } from './markdown-content';
import { ExecutionTree, type ExecutionTreeNode } from './execution-tree';
import { UserMessageTokens } from './user-message-tokens';
import { isDownGesture } from '../lib/scroll-gesture';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: string;
  metadata?: Record<string, unknown>;
  thinking_content?: string | null;
}

/**
 * How close to the bottom still counts as "following the conversation". Above
 * this distance the user is reading back, and auto-scroll stands down.
 */
const NEAR_BOTTOM_PX = 120;
/** scrollTop band that arms the upward page fire (the top sentinel). */
const TOP_FIRE_PX = 80;

/**
 * Ceiling on the auto-growing composer, as a fraction of the viewport height.
 * MUST stay in step with the textarea's `max-h-[40vh]` class: the class is the
 * backstop that applies before the resize effect has run, this constant is what
 * the effect clamps to.
 */
const COMPOSER_MAX_VH = 0.4;

/** Honour the OS reduced-motion setting for programmatic scrolling. */
function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function ArrowDownIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14" />
      <path d="m19 12-7 7-7-7" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
    </svg>
  );
}

function RetryIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
    </svg>
  );
}

export interface MentionRef {
  triggerId: string;
  itemId: string;
  label: string;
}

/**
 * DataTransfer MIME type carrying a serialized {@link MentionRef} when a file
 * tree node is dragged onto the composer. A foreign drag (any other payload)
 * carries no data under this key and is ignored.
 */
export const MENTION_DND_MIME = 'application/x-localagent-mention';

export interface TriggerItem {
  id: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
}

/** Labeled group inside the `@` / trigger dropdown (e.g. "From browser"). */
export interface TriggerSection {
  id: string;
  label: string;
  items: TriggerItem[];
}

/** Status / error banner above trigger results (labeled states). */
export interface TriggerBanner {
  kind: 'info' | 'warning' | 'error';
  message: string;
  /** Stable test id for e2e / functional assertions. */
  testId?: string;
}

/**
 * MIME types the composer accepts for image attachment, matching the server's
 * accepted set. A dropped or pasted file outside it is refused visibly.
 */
const COMPOSER_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
] as const;

export interface TriggerConfig {
  char: string;
  label: string;
  items: TriggerItem[];
  /**
   * When false, `items` / `sections` are already query-resolved (search/glob);
   * the dropdown does not apply client-side `includes()` filtering. Default true
   * preserves legacy static-catalog triggers.
   */
  clientFilter?: boolean;
  /** Labeled status banners (Indexing…, glob/search leg errors). */
  banners?: TriggerBanner[];
  /**
   * When true, selecting an item inserts `<char><label> ` as editable TEXT at
   * the trigger position and keeps the composer focused (a text completion),
   * instead of the default mention-pill insertion. Used by `/` and `#` —
   * send is a normal user prompt; the inserted literal stays editable.
   */
  insertAsText?: boolean;
  /** Labeled item groups; when set, rendered instead of the flat `items` list. */
  sections?: TriggerSection[];
  /** Shown when the parent reports search success with zero hits. */
  emptyMessage?: string;
  /** Debounced search parent: called whenever the in-trigger query changes. */
  onQueryChange?: (query: string) => void;
  onSelect?: (item: TriggerItem) => void;
  renderItem?: (item: TriggerItem) => React.ReactNode;
}

export interface ChatInterfaceProps {
  messages: ChatMessage[];
  onSendMessage: (content: string, mentions?: MentionRef[]) => void;
  onEditMessage?: (messageId: string, newContent: string) => void;
  onRetryMessage?: (messageId: string, content: string) => void;
  triggers?: TriggerConfig[];
  isLoading?: boolean;
  loadingContent?: React.ReactNode;
  placeholder?: string;
  renderMessage?: (message: ChatMessage) => React.ReactNode;
  /** Rendered immediately before each message bubble inside the scroll region. */
  renderBeforeMessage?: (message: ChatMessage) => React.ReactNode;
  /** Shown in place of the thread when there are no messages and none are loading. */
  emptyState?: React.ReactNode;
  renderMessageFooter?: (message: ChatMessage) => React.ReactNode;
  headerSlot?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  /** Called when an assistant message is clicked (for bidirectional run highlighting). */
  onMessageClick?: (messageId: string) => void;
  /** Slot rendered between the message list and input area (e.g. execution control bar). */
  aboveInputSlot?: React.ReactNode;
  /**
   * Slot rendered INSIDE the scrolling message region, after the messages and
   * `loadingContent`. Content that grows without bound (the agent activity
   * trail) belongs here, not in `aboveInputSlot`: an `aboveInputSlot` child has
   * `overflow: visible`, so its CSS automatic minimum size is its content
   * height and it CANNOT shrink — a tall one collapses the message list to zero
   * and then pushes the composer out of the flex box entirely (clipped by the
   * shell's `overflow-hidden`, so the user can neither read the reply nor type).
   */
  belowMessagesSlot?: React.ReactNode;
  /**
   * A monotonically-changing number that means "new content landed in
   * `belowMessagesSlot`" (e.g. the trail's row count). The auto-scroll effect
   * keys on it in addition to `messages.length`, so a live activity trail
   * follows; without it the trail grows below the fold unwatched.
   */
  scrollSignal?: number;
  /**
   * Fired when the top sentinel enters the viewport — the upward-paging hook.
   * Absent or `hasOlder` false →
   * no sentinel is rendered.
   */
  onLoadOlder?: () => void;
  /** While true the sentinel does not re-fire and a loading hint shows. */
  loadingOlder?: boolean;
  /** False hides the sentinel and the loading hint (session start reached). */
  hasOlder?: boolean;
  /** Fired when a downward scroll brings the released-history gap into view. */
  onLoadNewer?: () => void;
  loadingNewer?: boolean;
  hasNewer?: boolean;
  /** Released history between the loaded history and the live tail, shown after `afterMessageId`. */
  gap?: { afterMessageId: string; label: string };
  /** The ids of the messages on screen, oldest first, settled per frame; `null` on unmount. */
  onVisibleMessagesChange?: (visibleMessageIds: readonly string[] | null) => void;
  /** Called when the input value changes (for tracking whether user has typed
   *  text). Receives the caret offset so a caller can detect a token-boundary
   *  trigger relative to the cursor (skills-anywhere). */
  onInputChange?: (value: string, selectionStart?: number) => void;
  /** When true, the send button and Enter key remain enabled even while isLoading is true (e.g., for queueing messages). */
  allowSendWhileLoading?: boolean;
  /**
   * Prefix applied to the composer test IDs (`chat-input`, `send-button`) so a
   * SECOND ChatInterface mounted alongside the primary chat (e.g. the Shogun
   * panel, which auto-opens over the main `/chat` view) does not collide on
   * those selectors — an unscoped `[data-testid="chat-input"]` would otherwise
   * resolve to two elements. Default '' preserves the canonical `chat-input` /
   * `send-button` for the primary chat and every existing test/story.
   */
  testIdPrefix?: string;
  /**
   * Called with the image files a user pasted, dropped, or picked. Absent → the
   * composer shows no attach button and ignores image paste/drop entirely, so a
   * surface that cannot store images never offers to.
   */
  onAttachImages?: (files: File[], source: 'paste' | 'upload') => void;
  /** Called with the names of non-image files the user tried to attach. */
  onRejectAttachments?: (names: string[]) => void;
  /** Rendered directly above the composer row — the attachment chips. */
  attachments?: React.ReactNode;
  /**
   * How many attachments the chips represent. The composer needs the COUNT (not
   * just the node) because an image with no typed text is a sendable message.
   */
  attachmentCount?: number;
  /** Rendered inside a user bubble under its text — the sent image placeholders. */
  renderMessageImages?: (message: ChatMessage) => React.ReactNode;
  /** When true, Enter and the send button stay disabled (an upload is in flight). */
  sendBlocked?: boolean;
}

/** Imperative handle for the chat composer. */
export interface ChatInterfaceHandle {
  /**
   * Restore text into the composer and focus it — used to recover a message
   * whose send failed (the composer clears on send, so a failed first-send
   * would otherwise silently drop the user's text).
   */
  restoreInput: (value: string) => void;
}

function PaperclipIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13.234 20.252 21 12.3" />
      <path d="m16 6-8.414 8.586a2 2 0 0 0 0 2.828 2 2 0 0 0 2.828 0l8.414-8.586a4 4 0 0 0 0-5.656 4 4 0 0 0-5.656 0l-8.415 8.585a6 6 0 1 0 8.486 8.486" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
      <path d="m21.854 2.147-10.94 10.939" />
    </svg>
  );
}

function ThinkingIndicator() {
  return (
    <div className="w-full rounded-lg bg-message-agent px-4 py-3">
      <div className="flex gap-1">
        <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/50" style={{ animationDelay: '0ms' }} />
        <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/50" style={{ animationDelay: '150ms' }} />
        <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/50" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  );
}

interface PlannerStepInfo {
  type: 'llm_call' | 'tool_call';
  iteration: number;
  tool_name?: string;
  tool_result_status?: string;
}

function ActivityLog({ steps }: { steps: PlannerStepInfo[] }) {
  const [open, setOpen] = useState(false);
  const toolSteps = steps.filter((s) => s.type === 'tool_call');
  const iterations = new Set(steps.map((s) => s.iteration)).size;

  const treeNodes: ExecutionTreeNode[] = steps.map((step, idx) => ({
    id: `step-${idx}`,
    label: step.type === 'llm_call' ? `LLM call (iter ${step.iteration + 1})` : (step.tool_name ?? 'tool'),
    badge: step.type === 'llm_call' ? 'LLM' : 'tool',
    status: step.type === 'tool_call'
      ? (step.tool_result_status === 'error' ? 'failed' : 'completed')
      : 'completed',
  }));

  return (
    <div className="mt-2 border-t pt-1.5">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-caption text-muted-foreground hover:text-foreground"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${open ? 'rotate-90' : ''}`}>
          <path d="m9 18 6-6-6-6" />
        </svg>
        Activity ({iterations} iteration{iterations !== 1 ? 's' : ''}, {toolSteps.length} tool call{toolSteps.length !== 1 ? 's' : ''})
      </button>
      {open && (
        <div className="mt-1">
          <ExecutionTree nodes={treeNodes} />
        </div>
      )}
    </div>
  );
}

interface GraphNodeInfo {
  id: string;
  label?: string;
  persona_name?: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
}

function GraphNodesSection({ nodes, description }: { nodes: GraphNodeInfo[]; description?: string }) {
  const treeNodes: ExecutionTreeNode[] = nodes.map((node) => ({
    id: node.id,
    label: node.label ?? node.id,
    badge: node.persona_name,
    status: node.status,
  }));

  const completedCount = nodes.filter((n) => n.status === 'completed').length;

  return (
    <div className="mt-2 border-t pt-1.5">
      {description && (
        <p className="mb-1 text-caption font-medium text-muted-foreground">{description}</p>
      )}
      <ExecutionTree nodes={treeNodes} />
      <p className="mt-1 text-caption text-muted-foreground">
        {completedCount}/{nodes.length} nodes completed
      </p>
    </div>
  );
}

const DefaultMessageBubble = React.memo(function DefaultMessageBubble({
  message,
  onEditMessage,
  onRetryMessage,
  editingDisabled,
  onMessageClick,
  renderMessageImages,
}: {
  message: ChatMessage;
  onEditMessage?: (messageId: string, newContent: string) => void;
  onRetryMessage?: (messageId: string, content: string) => void;
  editingDisabled?: boolean;
  onMessageClick?: (messageId: string) => void;
  renderMessageImages?: (message: ChatMessage) => React.ReactNode;
}) {
  const isUser = message.role === 'user';
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content);
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && editTextareaRef.current) {
      editTextareaRef.current.focus();
      editTextareaRef.current.style.height = 'auto';
      editTextareaRef.current.style.height = `${editTextareaRef.current.scrollHeight}px`;
    }
  }, [isEditing]);

  const handleSave = () => {
    const trimmed = editContent.trim();
    if (!trimmed) return;
    if (trimmed !== message.content) {
      onEditMessage?.(message.id, trimmed);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditContent(message.content);
    setIsEditing(false);
  };

  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const canEdit = isUser && onEditMessage && !editingDisabled;
  const canRetry = isUser && onRetryMessage && !editingDisabled;
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may not be available
    }
  };

  return (
    <div
      className={cn(
        'group flex w-full flex-col items-stretch rounded-lg',
        isUser ? 'bg-message-user' : 'bg-message-agent',
        !isUser && onMessageClick && 'cursor-pointer',
      )}
      data-testid={`message-${message.role}`}
      data-message-id={message.id}
      onClick={!isUser && onMessageClick ? () => onMessageClick(message.id) : undefined}
    >
      <div
        className={cn('w-full px-4 pt-3 pb-1', isUser && 'text-foreground')}
      >
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              ref={editTextareaRef}
              value={editContent}
              onChange={(e) => {
                setEditContent(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = `${e.target.scrollHeight}px`;
              }}
              onKeyDown={handleEditKeyDown}
              className="w-full resize-none rounded-md bg-background px-2 py-1 text-chat text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              rows={1}
            />
            <div className="flex justify-end gap-1">
              <button
                type="button"
                onClick={handleCancel}
                className="rounded p-1 text-muted-foreground hover:text-foreground"
                aria-label="Cancel edit"
              >
                <XIcon />
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!editContent.trim()}
                className="rounded p-1 text-muted-foreground hover:text-foreground disabled:opacity-40"
                aria-label="Save edit"
              >
                <CheckIcon />
              </button>
            </div>
          </div>
        ) : (
          <>
            {isUser ? (
              <UserMessageTokens text={message.content} />
            ) : (
              <MarkdownContent content={message.content} className="text-chat" />
            )}
            {isUser && renderMessageImages?.(message)}
            {message.timestamp && <p className="mt-1 text-caption opacity-60">{message.timestamp}</p>}
            {!isUser && message.metadata?.graph_nodes && Array.isArray(message.metadata.graph_nodes) && message.metadata.graph_nodes.length > 0 && (
              <GraphNodesSection
                nodes={message.metadata.graph_nodes as GraphNodeInfo[]}
                description={message.metadata.graph_description as string | undefined}
              />
            )}
            {!isUser && message.metadata?.planner_steps && Array.isArray(message.metadata.planner_steps) && message.metadata.planner_steps.length > 0 && (
              <ActivityLog steps={message.metadata.planner_steps as PlannerStepInfo[]} />
            )}
            {!isUser && message.metadata?.context_metadata && (() => {
              const ctx = message.metadata.context_metadata as Record<string, unknown>;
              const summarized = (ctx.total_messages_summarized as number) || 0;
              const reduced = (ctx.total_tokens_reduced as number) || 0;
              if (summarized === 0 && reduced === 0) return null;
              return (
                <div
                  data-testid="context-event-notification"
                  className="inline-flex items-center gap-1.5 rounded-full bg-warning-subtle px-2.5 py-0.5 text-micro text-warning-text mt-1"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 16v-4" /><path d="M12 8h.01" /><circle cx="12" cy="12" r="10" />
                  </svg>
                  <span data-testid="context-event-text">
                    Context managed: {[
                      summarized > 0 ? `${summarized} message${summarized !== 1 ? 's' : ''} summarized` : '',
                      reduced > 0 ? `${reduced >= 1000 ? `${Math.round(reduced / 1000)}K` : reduced} tokens saved` : '',
                    ].filter(Boolean).join(', ')}
                  </span>
                </div>
              );
            })()}
          </>
        )}
      </div>
      {/* Action bar — appears on hover below the bubble */}
      {!isEditing && (
        <div className="flex gap-1 px-3 pb-2 opacity-0 transition-opacity group-hover:opacity-100">
          <button
            type="button"
            onClick={() => void handleCopy()}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Copy message"
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
          </button>
          {canEdit && (
            <button
              type="button"
              onClick={() => {
                setEditContent(message.content);
                setIsEditing(true);
              }}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Edit message"
            >
              <PencilIcon />
            </button>
          )}
          {canRetry && (
            <button
              type="button"
              onClick={() => onRetryMessage?.(message.id, message.content)}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Retry message"
            >
              <RetryIcon />
            </button>
          )}
        </div>
      )}
    </div>
  );
});

export const ChatInterface = forwardRef<ChatInterfaceHandle, ChatInterfaceProps>(function ChatInterface({
  messages,
  onSendMessage,
  onEditMessage,
  onRetryMessage,
  triggers,
  isLoading,
  loadingContent,
  placeholder = 'Type a message...',
  renderMessage,
  renderBeforeMessage,
  emptyState,
  renderMessageFooter,
  headerSlot,
  className,
  disabled,
  onMessageClick,
  aboveInputSlot,
  belowMessagesSlot,
  scrollSignal,
  onLoadOlder,
  loadingOlder,
  hasOlder,
  onLoadNewer,
  loadingNewer,
  hasNewer,
  gap,
  onVisibleMessagesChange,
  onInputChange,
  allowSendWhileLoading,
  testIdPrefix = '',
  onAttachImages,
  onRejectAttachments,
  attachments,
  attachmentCount = 0,
  renderMessageImages,
  sendBlocked,
}: ChatInterfaceProps, ref) {
  const [inputValue, setInputValue] = useState('');
  const [activeMentions, setActiveMentions] = useState<MentionRef[]>([]);
  // Resolve the live TriggerConfig from `triggers` by char so async parent
  // updates (search/glob results, banners) refresh the open dropdown.
  const [activeTriggerChar, setActiveTriggerChar] = useState<string | null>(null);
  const [triggerQuery, setTriggerQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Whether the viewport is parked at (or near) the bottom. Auto-scroll only
  // fires when it is — otherwise reading back through a long transcript is
  // impossible, because every arriving event yanks the view to the end.
  const [isNearBottom, setIsNearBottom] = useState(true);

  const activeTrigger = useMemo(
    () => (activeTriggerChar ? triggers?.find((t) => t.char === activeTriggerChar) ?? null : null),
    [activeTriggerChar, triggers],
  );

  useImperativeHandle(ref, () => ({
    restoreInput: (value: string) => {
      setInputValue(value);
      onInputChange?.(value);
      textareaRef.current?.focus();
    },
  }), [onInputChange]);

  // Upward paging: an UPWARD scroll
  // (scrollTop decreasing) that reaches the top band fires onLoadOlder, so the
  // open-at-bottom descent (increasing scrollTop) can never spuriously fire.
  // Geometry is captured at fire time so the prepend can be compensated.
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const prevFirstMessageIdRef = useRef<string | undefined>(undefined);
  const scrollAnchorRef = useRef<{ scrollTop: number; scrollHeight: number; firstMessageId?: string } | null>(null);
  const loadingOlderRef = useRef(loadingOlder);
  loadingOlderRef.current = loadingOlder;
  const onLoadOlderRef = useRef(onLoadOlder);
  onLoadOlderRef.current = onLoadOlder;
  const hasOlderRef = useRef(hasOlder);
  hasOlderRef.current = hasOlder;
  const lastScrollTopRef = useRef(0);
  const gapRef = useRef<HTMLDivElement>(null);
  const ownScrollRef = useRef(true);
  const newerRef = useRef({ onLoadNewer, loadingNewer, hasNewer });
  newerRef.current = { onLoadNewer, loadingNewer, hasNewer };
  const onVisibleRef = useRef(onVisibleMessagesChange);
  onVisibleRef.current = onVisibleMessagesChange;
  const visibleFrameRef = useRef<number | null>(null);

  const reportVisible = useCallback(() => {
    if (!onVisibleRef.current || visibleFrameRef.current !== null) return;
    visibleFrameRef.current = requestAnimationFrame(() => {
      visibleFrameRef.current = null;
      const el = scrollRef.current;
      if (!el || !onVisibleRef.current) return;
      const box = el.getBoundingClientRect();
      const visible: string[] = [];
      for (const row of el.querySelectorAll<HTMLElement>('[data-message-id]')) {
        const rect = row.getBoundingClientRect();
        if (rect.bottom <= box.top || rect.top >= box.bottom) continue;
        visible.push(row.dataset.messageId!);
      }
      onVisibleRef.current(visible);
    });
  }, []);

  useEffect(() => {
    reportVisible();
  }, [messages, reportVisible]);

  const loadNewerIfGapInView = useCallback((el: HTMLElement) => {
    const newer = newerRef.current;
    const gapEl = gapRef.current;
    if (!newer.onLoadNewer || !newer.hasNewer || newer.loadingNewer || !gapEl) return;
    const box = el.getBoundingClientRect();
    const rect = gapEl.getBoundingClientRect();
    if (rect.top < box.bottom && rect.bottom > box.top) newer.onLoadNewer();
  }, []);

  // (sdk ADR-f43c7)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const byReader = () => {
      ownScrollRef.current = false;
    };
    const onScrollEnd = () => {
      if (el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX) ownScrollRef.current = false;
      if (!ownScrollRef.current) loadNewerIfGapInView(el);
    };
    const byReaderDown = (e: WheelEvent | KeyboardEvent) => {
      ownScrollRef.current = false;
      if (isDownGesture(e) && el.scrollHeight <= el.clientHeight) loadNewerIfGapInView(el);
    };
    el.addEventListener('scrollend', onScrollEnd);
    el.addEventListener('wheel', byReaderDown, { passive: true });
    el.addEventListener('touchstart', byReader, { passive: true });
    el.addEventListener('pointerdown', byReader);
    el.addEventListener('keydown', byReaderDown);
    return () => {
      el.removeEventListener('scrollend', onScrollEnd);
      el.removeEventListener('wheel', byReaderDown);
      el.removeEventListener('touchstart', byReader);
      el.removeEventListener('pointerdown', byReader);
      el.removeEventListener('keydown', byReaderDown);
    };
  }, [loadNewerIfGapInView]);

  useEffect(
    () => () => {
      if (visibleFrameRef.current !== null) cancelAnimationFrame(visibleFrameRef.current);
      visibleFrameRef.current = null;
      onVisibleRef.current?.(null);
    },
    [],
  );

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const scrollTop = el.scrollTop;
      setIsNearBottom(el.scrollHeight - scrollTop - el.clientHeight < NEAR_BOTTOM_PX);
      if (
        onLoadOlderRef.current &&
        hasOlderRef.current &&
        !loadingOlderRef.current &&
        scrollTop < TOP_FIRE_PX &&
        scrollTop < lastScrollTopRef.current - 1
      ) {
        scrollAnchorRef.current = { scrollTop, scrollHeight: el.scrollHeight, firstMessageId: prevFirstMessageIdRef.current };
        onLoadOlderRef.current();
      }
      lastScrollTopRef.current = scrollTop;
      reportVisible();
    },
    [reportVisible],
  );

  const scrollToLatest = useCallback(() => {
    ownScrollRef.current = true;
    messagesEndRef.current?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    setIsNearBottom(true);
  }, []);

  // Auto-scroll on new messages AND on new below-messages content (the activity
  // trail), but only while the user is already at the bottom — see isNearBottom.
  useEffect(() => {
    if (!isNearBottom) return;
    ownScrollRef.current = true;
    messagesEndRef.current?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    // NOTE: `isNearBottom` is read but deliberately NOT a dependency — this
    // effect reacts to new CONTENT, not to the user's scrolling (which updates
    // it on every wheel tick and would re-fire the scroll on each one).
  }, [messages.length, isLoading, scrollSignal]);

  // Upward paging: the top sentinel
  // fires onLoadOlder when it intersects, suppressed while a page is in flight.
  // Geometry is captured at fire time so the prepend can be compensated.
  // Scroll preservation on prepend: when the first message identity
  // changes while a load-older anchor is held, compensate scrollTop by the
  // height delta — upward growth without compensation is the classic
  // infinite-scroll-up jump bug.
  const firstMessageId = messages[0]?.id;
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const anchor = scrollAnchorRef.current;
    if (el && anchor && firstMessageId !== anchor.firstMessageId) {
      if (messages.some((m) => m.id === anchor.firstMessageId)) {
        el.scrollTop = anchor.scrollTop + (el.scrollHeight - anchor.scrollHeight);
      }
      scrollAnchorRef.current = null;
    }
    prevFirstMessageIdRef.current = firstMessageId;
  }, [firstMessageId, messages]);

  // Auto-resize textarea, bounded. Unbounded growth is the composer's own
  // version of the trail bug: a long draft pushes the send button off screen.
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const maxPx = window.innerHeight * COMPOSER_MAX_VH;
      textarea.style.height = `${Math.min(textarea.scrollHeight, maxPx)}px`;
    }
  }, [inputValue]);

  // Notify the parent when the in-trigger query changes (debounced search).
  // Ref-stable so parent TriggerConfig identity churn does not re-fire.
  const onTriggerQueryChangeRef = useRef<TriggerConfig['onQueryChange']>(undefined);
  onTriggerQueryChangeRef.current = activeTrigger?.onQueryChange;
  useEffect(() => {
    if (!activeTriggerChar) return;
    onTriggerQueryChangeRef.current?.(triggerQuery);
  }, [activeTriggerChar, triggerQuery]);

  const filteredTriggerItems = useMemo(() => {
    if (!activeTrigger) return [];
    const source = activeTrigger.sections
      ? activeTrigger.sections.flatMap((s) => s.items)
      : activeTrigger.items;
    if (activeTrigger.clientFilter === false) return source;
    const query = triggerQuery.toLowerCase();
    if (!query) return source;
    return source.filter(
      (item) => item.label.toLowerCase().includes(query) || item.description?.toLowerCase().includes(query),
    );
  }, [activeTrigger, triggerQuery]);

  const showTriggerDropdown = Boolean(
    activeTrigger &&
      (filteredTriggerItems.length > 0 ||
        (activeTrigger.banners && activeTrigger.banners.length > 0) ||
        activeTrigger.emptyMessage ||
        (activeTrigger.sections && activeTrigger.sections.length > 0)),
  );

  const selectTriggerItem = useCallback(
    (item: TriggerItem) => {
      if (!activeTrigger) return;
      activeTrigger.onSelect?.(item);

      if (activeTrigger.insertAsText) {
        // Text completion: replace the `/query` run with `<char><label> ` as
        // literal editable text, keep the composer focused, then move the caret
        // after the inserted token so the user can continue typing prose.
        const triggerStart = inputValue.lastIndexOf(activeTrigger.char);
        if (triggerStart >= 0) {
          const caret = textareaRef.current?.selectionStart ?? inputValue.length;
          const insertEnd = Math.max(caret, triggerStart + 1);
          const insertion = `${activeTrigger.char}${item.label} `;
          const newValue =
            inputValue.slice(0, triggerStart) + insertion + inputValue.slice(insertEnd);
          setInputValue(newValue);
          onInputChange?.(newValue);
          const newCaret = triggerStart + insertion.length;
          requestAnimationFrame(() => {
            textareaRef.current?.focus();
            textareaRef.current?.setSelectionRange(newCaret, newCaret);
          });
        }
      } else {
        // Default: insert a mention pill and remove the trigger char + query.
        const mention: MentionRef = { triggerId: activeTrigger.char, itemId: item.id, label: item.label };
        setActiveMentions((prev) => [...prev, mention]);
        const triggerStart = inputValue.lastIndexOf(activeTrigger.char);
        const newValue = triggerStart >= 0 ? inputValue.slice(0, triggerStart) : inputValue;
        setInputValue(newValue);
        onInputChange?.(newValue);
      }

      setActiveTriggerChar(null);
      setTriggerQuery('');
      setHighlightedIndex(0);
      textareaRef.current?.focus();
    },
    [activeTrigger, inputValue, onInputChange],
  );

  const removeMention = useCallback((index: number) => {
    setActiveMentions((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Add a mention (e.g. from a drag-drop of a file tree node), deduped by
  // (triggerId, itemId) so dropping the same file twice yields one pill.
  const addMention = useCallback((mention: MentionRef) => {
    setActiveMentions((prev) =>
      prev.some((m) => m.triggerId === mention.triggerId && m.itemId === mention.itemId)
        ? prev
        : [...prev, mention],
    );
  }, []);

  // Split a FileList by whether the composer accepts it: the images go to the
  // owner, the rest are named back so the refusal is visible rather than a
  // silently ignored drop.
  const routeAttachments = useCallback(
    (files: File[], source: 'paste' | 'upload') => {
      if (!onAttachImages || files.length === 0) return;
      const images = files.filter((f) =>
        (COMPOSER_IMAGE_TYPES as readonly string[]).includes(f.type),
      );
      const rejected = files.filter((f) => !images.includes(f));
      if (images.length > 0) onAttachImages(images, source);
      if (rejected.length > 0) {
        onRejectAttachments?.(rejected.map((f) => f.name || f.type || 'unnamed file'));
      }
    },
    [onAttachImages, onRejectAttachments],
  );

  const handleSend = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed && activeMentions.length === 0 && attachmentCount === 0) return;
    if (sendBlocked) return;
    onSendMessage(trimmed, activeMentions.length > 0 ? activeMentions : undefined);
    setInputValue('');
    onInputChange?.('');
    setActiveMentions([]);
    setActiveTriggerChar(null);
    setTriggerQuery('');
  }, [inputValue, activeMentions, attachmentCount, sendBlocked, onSendMessage, onInputChange]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      const caret = e.target.selectionStart ?? value.length;
      setInputValue(value);
      onInputChange?.(value, caret);

      if (!triggers || triggers.length === 0) return;

      // Activate a trigger when its char sits immediately before the caret
      // (REQ-C2: any caret, including inside a word or URL).
      const charBeforeCaret = caret >= 1 ? value[caret - 1] : '';
      const activeStart = activeTriggerChar ? value.lastIndexOf(activeTriggerChar, caret - 2) : -1;
      const extendsActiveRun =
        activeStart >= 0 && !/\s/.test(value.slice(activeStart + 1, caret));
      const matchedTrigger = charBeforeCaret && !extendsActiveRun
        ? triggers.find((t) => t.char === charBeforeCaret)
        : undefined;

      if (matchedTrigger) {
        setActiveTriggerChar(matchedTrigger.char);
        setTriggerQuery('');
        setHighlightedIndex(0);
        return;
      }

      if (activeTriggerChar) {
        const triggerStart = value.lastIndexOf(activeTriggerChar);
        if (triggerStart >= 0) {
          // The live query is the run between the trigger char and the caret.
          const query = value.slice(triggerStart + 1, caret);
          if (query.includes(' ') && query.length > 20) {
            // Dismiss trigger if query is too long with spaces
            setActiveTriggerChar(null);
            setTriggerQuery('');
          } else {
            setTriggerQuery(query);
            setHighlightedIndex(0);
          }
        } else {
          setActiveTriggerChar(null);
          setTriggerQuery('');
        }
      }
    },
    [triggers, activeTriggerChar, onInputChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Handle trigger dropdown navigation (items) or Escape when only banners/empty.
      if (activeTrigger && showTriggerDropdown) {
        if (filteredTriggerItems.length > 0) {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex((prev) => (prev + 1) % filteredTriggerItems.length);
            return;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex((prev) => (prev - 1 + filteredTriggerItems.length) % filteredTriggerItems.length);
            return;
          }
          if (e.key === 'Enter') {
            e.preventDefault();
            selectTriggerItem(filteredTriggerItems[highlightedIndex]);
            return;
          }
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setActiveTriggerChar(null);
          setTriggerQuery('');
          return;
        }
      }

      // Send on Enter (without Shift)
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (!disabled && (!isLoading || allowSendWhileLoading)) handleSend();
      }
    },
    [activeTrigger, showTriggerDropdown, filteredTriggerItems, highlightedIndex, selectTriggerItem, disabled, isLoading, allowSendWhileLoading, handleSend],
  );

  return (
    <div className={cn('relative flex h-full flex-col', className)}>
      {/* Header slot */}
      {headerSlot && <div className="shrink-0 px-4 py-3">{headerSlot}</div>}

      {/* Wrapper exists to anchor the jump-to-latest pill to the SCROLL REGION
          rather than the whole panel (whose bottom moves as the composer grows).
          `min-h-0` is load-bearing here and NOT redundant: this wrapper's
          overflow is `visible`, so without it its automatic minimum size is its
          content height and it would refuse to shrink — the same mechanism that
          caused the original composer-pushed-off-screen bug. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
      {/* Message list — THE scroll region. Everything that can grow without
          bound must live in here (see belowMessagesSlot's doc comment). */}
      <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          {onLoadOlder && hasOlder ? (
            <div ref={topSentinelRef} className="h-px" data-testid={`${testIdPrefix}load-older-sentinel`} />
          ) : null}
          {loadingOlder ? (
            <div
              className="pointer-events-none absolute inset-x-0 top-0 z-10 bg-secondary/70 py-2 text-center text-caption text-muted-foreground"
              data-testid={`${testIdPrefix}loading-older`}
            >
              Loading older messages…
            </div>
          ) : null}
          {emptyState && messages.length === 0 && !isLoading ? (
            <div data-testid="chat-empty-state">{emptyState}</div>
          ) : null}
          {messages.map((message) => (
            <React.Fragment key={message.id}>
              {renderBeforeMessage?.(message)}
              {renderMessage?.(message) ?? (
                <DefaultMessageBubble message={message} onEditMessage={onEditMessage} onRetryMessage={onRetryMessage} editingDisabled={disabled || isLoading} onMessageClick={onMessageClick} renderMessageImages={renderMessageImages} />
              )}
              {renderMessageFooter?.(message)}
              {gap && gap.afterMessageId === message.id ? (
                <div
                  ref={gapRef}
                  className="flex items-center justify-center gap-3 rounded-md bg-secondary/60 px-3 py-2 text-caption text-muted-foreground"
                  data-testid={`${testIdPrefix}transcript-gap`}
                >
                  <span>{loadingNewer ? 'Loading newer messages…' : gap.label}</span>
                  <button
                    type="button"
                    onClick={scrollToLatest}
                    data-testid={`${testIdPrefix}transcript-gap-jump`}
                    className="font-medium text-foreground underline"
                  >
                    Jump to latest
                  </button>
                </div>
              ) : null}
            </React.Fragment>
          ))}
          {isLoading && (loadingContent ?? <ThinkingIndicator />)}
          {belowMessagesSlot}
          <div ref={messagesEndRef} />
        </div>
      </div>

        {/* Jump to latest — the counterpart to the near-bottom auto-scroll
            guard. Without it, standing down from auto-scroll leaves the view
            feeling stuck with no way back to the live end of the conversation. */}
        {!isNearBottom && (
          <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
            <button
              type="button"
              onClick={scrollToLatest}
              data-testid={`${testIdPrefix}jump-to-latest`}
              className="pointer-events-auto flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-caption font-medium text-secondary-foreground shadow-md hover:bg-secondary/80"
            >
              <ArrowDownIcon />
              Jump to latest
            </button>
          </div>
        )}
      </div>

      {/* Above-input slot (e.g. execution control bar). `shrink-0` is intent:
          this region is for small, fixed affordances only. */}
      {aboveInputSlot && <div className="shrink-0">{aboveInputSlot}</div>}

      {/* Input area */}
      <div className="shrink-0 p-4">
        <div>
          {attachments}

          {/* Mention pills */}
          {activeMentions.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {activeMentions.map((mention, idx) => (
                <span key={`${mention.triggerId}-${mention.itemId}-${idx}`} className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-caption font-medium text-primary">
                  {mention.triggerId}
                  {mention.label}
                  <button
                    type="button"
                    className="ml-0.5 rounded-full hover:bg-primary/20"
                    onClick={() => removeMention(idx)}
                    aria-label={`Remove ${mention.label}`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6 6 18" />
                      <path d="m6 6 12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Input row with trigger dropdown */}
          <div className="relative flex items-end gap-2">
            {/* Trigger dropdown — items, labeled sections, banners, empty */}
            {showTriggerDropdown && activeTrigger && (
              <div
                data-testid="mention-dropdown"
                className="absolute bottom-full left-0 mb-2 max-h-[min(20rem,50vh)] w-72 overflow-y-auto rounded-md border bg-popover p-1 shadow-md"
              >
                <p className="px-2 py-1 text-caption font-medium text-muted-foreground">{activeTrigger.label}</p>
                {activeTrigger.banners?.map((banner, i) => (
                  <p
                    key={`banner-${i}-${banner.message}`}
                    data-testid={banner.testId ?? `mention-banner-${banner.kind}`}
                    className={cn(
                      'px-2 py-1 text-caption',
                      banner.kind === 'error' && 'text-destructive',
                      banner.kind === 'warning' && 'text-warning-text',
                      banner.kind === 'info' && 'text-muted-foreground',
                    )}
                  >
                    {banner.message}
                  </p>
                ))}
                {activeTrigger.sections && activeTrigger.sections.length > 0
                  ? (() => {
                      // Flat keyboard index across sections (same order as filteredTriggerItems).
                      let flatIdx = 0;
                      return activeTrigger.sections.map((section) => {
                        const sectionItems =
                          activeTrigger.clientFilter === false
                            ? section.items
                            : section.items.filter((item) => {
                                const q = triggerQuery.toLowerCase();
                                if (!q) return true;
                                return (
                                  item.label.toLowerCase().includes(q) ||
                                  item.description?.toLowerCase().includes(q)
                                );
                              });
                        // Always render the section shell: an empty
                        // "From browser" group must still be labeled — unlabeled
                        // browse-only degrade is a defect.
                        return (
                          <div key={section.id} data-testid={`mention-section-${section.id}`}>
                            <p className="px-2 pb-0.5 pt-1 text-micro font-semibold uppercase tracking-wide text-muted-foreground">
                              {section.label}
                            </p>
                            {sectionItems.length === 0 && (
                              <p className="px-2 py-1 text-caption text-muted-foreground">No browsed paths yet</p>
                            )}
                            {sectionItems.map((item) => {
                              const idx = flatIdx++;
                              return (
                                <button
                                  key={item.id}
                                  type="button"
                                  data-testid="mention-option"
                                  data-mention-path={item.id}
                                  className={cn(
                                    'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-body',
                                    idx === highlightedIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50',
                                  )}
                                  onClick={() => selectTriggerItem(item)}
                                  onMouseEnter={() => setHighlightedIndex(idx)}
                                >
                                  {activeTrigger.renderItem ? (
                                    activeTrigger.renderItem(item)
                                  ) : (
                                    <>
                                      {item.icon && <span className="shrink-0">{item.icon}</span>}
                                      <div className="min-w-0 text-left">
                                        <p className="truncate font-medium">{item.label}</p>
                                        {item.description && (
                                          <p className="truncate text-caption text-muted-foreground">{item.description}</p>
                                        )}
                                      </div>
                                    </>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        );
                      });
                    })()
                  : filteredTriggerItems.map((item, idx) => (
                      <button
                        key={item.id}
                        type="button"
                        data-testid="mention-option"
                        data-mention-path={item.id}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-body',
                          idx === highlightedIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50',
                        )}
                        onClick={() => selectTriggerItem(item)}
                        onMouseEnter={() => setHighlightedIndex(idx)}
                      >
                        {activeTrigger.renderItem ? (
                          activeTrigger.renderItem(item)
                        ) : (
                          <>
                            {item.icon && <span className="shrink-0">{item.icon}</span>}
                            <div className="min-w-0 text-left">
                              <p className="truncate font-medium">{item.label}</p>
                              {item.description && (
                                <p className="truncate text-caption text-muted-foreground">{item.description}</p>
                              )}
                            </div>
                          </>
                        )}
                      </button>
                    ))}
                {filteredTriggerItems.length === 0 && activeTrigger.emptyMessage && (
                  <p data-testid="mention-empty" className="px-2 py-2 text-caption text-muted-foreground">
                    {activeTrigger.emptyMessage}
                  </p>
                )}
              </div>
            )}

            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onDragOver={(e) => {
                e.preventDefault();
              }}
              onDrop={(e) => {
                e.preventDefault();
                const raw = e.dataTransfer.getData(MENTION_DND_MIME);
                if (raw) {
                  try {
                    const m = JSON.parse(raw) as MentionRef;
                    if (m && m.itemId) addMention(m);
                  } catch {
                    /* ignore malformed drag payloads (a foreign drag, not an error) */
                  }
                  return;
                }
                // No mention payload: an OS-file drop. Checked SECOND so the
                // internal file-tree drag keeps its existing behaviour.
                routeAttachments(Array.from(e.dataTransfer.files), 'upload');
              }}
              onPaste={(e) => {
                // Deliberately does NOT preventDefault: a paste carrying both
                // text and an image attaches the image and still inserts the
                // text.
                routeAttachments(Array.from(e.clipboardData.files), 'paste');
              }}
              placeholder={placeholder}
              disabled={disabled}
              rows={1}
              data-testid={`${testIdPrefix}chat-input`}
              className="max-h-[40vh] flex-1 resize-none overflow-y-auto rounded-md bg-muted px-3 py-2 text-body ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
            {onAttachImages && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={COMPOSER_IMAGE_TYPES.join(',')}
                  multiple
                  hidden
                  data-testid={`${testIdPrefix}attach-image-input`}
                  onChange={(e) => {
                    routeAttachments(Array.from(e.target.files ?? []), 'upload');
                    e.target.value = '';
                  }}
                />
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled}
                  aria-label="Attach images"
                  data-testid={`${testIdPrefix}attach-image-button`}
                >
                  <PaperclipIcon />
                </Button>
              </>
            )}
            <Button
              size="icon"
              onClick={handleSend}
              disabled={disabled || (isLoading && !allowSendWhileLoading) || sendBlocked || (!inputValue.trim() && activeMentions.length === 0 && attachmentCount === 0)}
              aria-label="Send message"
              data-testid={`${testIdPrefix}send-button`}
            >
              <SendIcon />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});
